import { getI18n } from '@/plugins/i18n'

export const getImportStatusFilter = (value, operation = '=') => {
  if (value === 'warning' && operation === '=') {
    return [['status', '=', 'warning'], 'or', [['status', '=', 'skipped'], 'and', ['message', '=', 'warning.import.duplicate']]]
  }
  return ['status', operation, value]
}

export const getImportResultPage = (trackType, trackTypes, routes) => ({
  [trackTypes.SUBJECT_CUSTOMER]: routes.SUBJECTS,
  [trackTypes.SUBJECT_SUPPLIER]: routes.SUBJECTS,
  [trackTypes.VEHICLE_TRANSIT]: routes.VEHICLES,
})[trackType] || null

export const getResultMessage = (row, fields) => {
  const i18n = getI18n()
  const messages = Object.entries(row.data || {})
    .filter(([, cell]) => (cell.status === 'error' || cell.status === 'warning') && cell.message)
    .map(([key, cell]) => {
      const field = fields.find(field => field.key === key)
      const message = i18n.te(cell.message) ? i18n.t(cell.message) : cell.message
      return `${field ? i18n.t(field.label) : key}: ${message}`
    })
  if (row.message === 'warning.import.duplicate') messages.push(i18n.t(row.message))
  if (messages.length) return messages.join('; ')
  return row.message && i18n.te(row.message) ? i18n.t(row.message) : row.message || ''
}

const identityFields = ['subject.name_ext', 'subject.name', 'subject.tax_code', 'subject.vat_number', 'vehicle.plate_number', 'vehicle.vin']

const resultFieldPriority = (key) => {
  const index = identityFields.indexOf(key)
  return index < 0 ? identityFields.length : index
}

const resultCellValue = (row, field, options, translate) => {
  const cell = Object.prototype.hasOwnProperty.call(row.source_row || {}, field.key) ? { value: row.source_row[field.key] } : row.data?.[field.key]
  if (cell?.value == null) return cell?.source_value ?? null
  if (field.data_type === 'boolean') return translate(`common.${cell.value ? 'yes' : 'no'}`)
  if (options?.type === 'lookup') {
    const item = options.dataSource?.find(item => String(item[options.valueExpr]) === String(cell.value))
    if (item) return typeof options.displayExpr === 'function' ? options.displayExpr(item) : item[options.displayExpr]
  }
  return cell.value
}

export const useColumns = ({ fields, activeFields, statuses, getEditorOptionsForField, editable = false }) => {
  const i18n = getI18n()
  const t = key => i18n.t(`common.importResult.${key}`)
  const nameFields = fields.filter(field => ['subject.name', 'subject.name_ext'].includes(field.key)).sort((a, b) => a.key.localeCompare(b.key))
  const orderedFields = [...fields].sort((a, b) => resultFieldPriority(a.key) - resultFieldPriority(b.key))

  return [
    { dataField: 'row_number', caption: t('row'), dataType: 'number', width: 80, fixed: true, sortOrder: 'asc', showInColumnChooser: false, allowFiltering: true, allowSorting: true },
    { dataField: 'status', caption: i18n.t('common.status'), lookup: { dataSource: statuses, valueExpr: 'id', displayExpr: 'name' }, calculateFilterExpression: getImportStatusFilter, cellTemplate: 'resultStatusTemplate', width: 130, fixed: true, showInColumnChooser: false, allowFiltering: true, allowSorting: true },
    ...(!editable && nameFields.length ? [{
      name: 'record_name',
      caption: t('recordName'),
      width: 260,
      visible: nameFields.some(field => activeFields.has(field.key)),
      calculateCellValue: row => nameFields.map(field => resultCellValue(row, field, null, i18n.t)).filter(value => value != null && value !== '').join(' '),
    }] : []),
    ...orderedFields.map(field => ({
      name: field.key,
      dataField: `corrections.${field.key.replaceAll('.', '_')}`,
      allowEditing: editable,
      editCellTemplate: 'correctionEditor',
      setCellValue: (data, value, row) => { data.source_row = { ...(row.source_row || {}), ...(data.source_row || {}), [field.key]: value } },
      caption: i18n.t(field.label),
      visible: activeFields.has(field.key) && !(editable ? ['subject.original_code', 'subject.code'] : ['subject.original_code', 'subject.code', 'subject.name', 'subject.name_ext']).includes(field.key),
      calculateCellValue: row => resultCellValue(row, field, getEditorOptionsForField(field), i18n.t),
    })),
    { dataField: 'message', caption: i18n.t('common.message'), width: 300, cellTemplate: 'resultMessageTemplate' },
  ].map(column => ({ allowSorting: false, allowFiltering: false, allowGrouping: false, ...column }))
}

export const getResultCell = (row, column) => {
  if (column.name !== 'record_name') return row.data?.[column.name]
  const cells = ['subject.name', 'subject.name_ext'].map(key => row.data?.[key])
  return cells.find(cell => cell?.status === 'error') || cells.find(cell => cell?.status === 'warning')
}
